// Pure helpers to normalize any recorded AudioBuffer into a 16 kHz mono
// 16-bit PCM WAV blob. Kept side-effect free so they are trivially testable.

export const TARGET_SAMPLE_RATE = 16000;

// Mix all channels down to one and resample to the target rate with linear
// interpolation. Returns a Float32Array of mono samples.
export const downsampleToMono = (audioBuffer, targetRate = TARGET_SAMPLE_RATE) => {
  const channelCount = audioBuffer.numberOfChannels || 1;
  const sourceRate = audioBuffer.sampleRate;
  const sourceLength = audioBuffer.length;

  const mono = new Float32Array(sourceLength);
  for (let channel = 0; channel < channelCount; channel += 1) {
    const data = audioBuffer.getChannelData(channel);
    for (let i = 0; i < sourceLength; i += 1) {
      mono[i] += data[i] / channelCount;
    }
  }

  if (sourceRate === targetRate) {
    return mono;
  }

  const ratio = sourceRate / targetRate;
  const targetLength = Math.max(1, Math.round(sourceLength / ratio));
  const output = new Float32Array(targetLength);
  for (let i = 0; i < targetLength; i += 1) {
    const position = i * ratio;
    const index = Math.floor(position);
    const fraction = position - index;
    const current = mono[index] || 0;
    const next = mono[index + 1] !== undefined ? mono[index + 1] : current;
    output[i] = current + fraction * (next - current);
  }
  return output;
};

// Encode mono Float32 samples as a 16-bit PCM RIFF/WAV ArrayBuffer.
export const encodeWav = (samples, sampleRate = TARGET_SAMPLE_RATE) => {
  const bytesPerSample = 2;
  const dataLength = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);

  const writeString = (offset, value) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true); // byte rate
  view.setUint16(32, bytesPerSample, true); // block align
  view.setUint16(34, 8 * bytesPerSample, true); // bits per sample
  writeString(36, "data");
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    offset += bytesPerSample;
  }
  return buffer;
};

// Decode a recorded blob and return a normalized WAV blob.
export const blobToWav = async (blob, targetRate = TARGET_SAMPLE_RATE) => {
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error("Web Audio API is not supported in this browser.");
  }
  const arrayBuffer = await blob.arrayBuffer();
  const context = new AudioContextCtor();
  try {
    const audioBuffer = await context.decodeAudioData(arrayBuffer);
    const mono = downsampleToMono(audioBuffer, targetRate);
    const wavBuffer = encodeWav(mono, targetRate);
    return new Blob([wavBuffer], { type: "audio/wav" });
  } finally {
    if (typeof context.close === "function") {
      context.close();
    }
  }
};
